import { createForm, type Field, type FieldNode, type Form, type LayoutNode, type Page, type SectionNode, type TabsNode } from '@fieldia/core';
import { uncalled } from './uncalled';
import { applyLook, labelPlace, planSection, quietHint, THEME_SKINS, type Place } from '@fieldia/viewer';
import { createWidget, wearQuiet, wearsQuiet, type Widget, type WidgetFactory } from '@fieldia/widgets';
import { advancedDrag } from './canvas-advanced';
import { blockViews } from './canvas-blocks';
import { formViews } from './canvas-form';
import { canvasDrag, type CanvasDrag } from './canvas-drag';
import { canvasHeader } from './canvas-header';
import { canvasKeys, type CanvasKeys } from './canvas-keys';
import { lockWords, type DesignerMode } from './canvas-mode';
import { canvasResize } from './canvas-resize';
import { columnsAt, readSize, readWidth, sizeSwitch, writeSize, writeWidth, type ScreenSize } from './canvas-size';
import { multiBar } from './canvas-multi';
import { canvasGuides } from './canvas-guides';
import { widthMarks } from './canvas-width';
import { elementFactory, optionsEditor, type OptionsEditor } from './chrome';
import type { Designer, DesignerState } from './designer';
import { fieldBar, type FieldBar } from './field-bar';
import { barsPlaced, stopWatchingBars } from './bar-place';
import { designerIcon } from './icons';
import { inlineSettings, type InlineSettings } from './inline-settings';
import { locate, rowsOf } from './layout-tree';
import { ruleMarks, sameMarks } from './rules-marks';
import { pressMarks } from './steps-marks';
import { tabHolds } from './page-tree';
import { sampleRows } from './samples';
import { foldMark, type FoldMark } from './canvas-fold';
import { growingBox } from './growing-box';
import { setAttr, setData, setHidden, setText } from './writes';
import { chromeLanguage, widgetWords } from './chrome-language';

/**
 * The screen editor's canvas: the page drawn the way the viewer draws it —
 * its sections in the viewer's grid, each field with its real widget, the
 * sheet's tabs as tabs — and edited where it stands. The field picked opens
 * in place: its label and help become boxes to type in, a choice's options
 * become a list to type, and a bar on its edge does the rest. Nothing around
 * it moves, so what is seen is what people will get.
 *
 * Groups inside groups, arrangements laying parts on their grid's columns,
 * each group's style, where labels sit, tabs anywhere and the blocks between
 * fields are drawn with the viewer's own elements and placed by its own rules
 * (`planSection`, `labelPlace`), and the canvas wears the page's look as the
 * form does — so the canvas and the form agree to the pixel.
 *
 * Everything is keyed by id and patched in place, so the box being typed in
 * stays the same box, focused, while the page changes around it.
 */

export interface ScreenCanvasOptions {
  designer: Designer;
  doc: Document;
  /** The skin the form is drawn in: the canvas wears its tokens, as the form does. */
  skin?: string;
  /** Open the panel at a part of the field picked: its settings, or when it shows. */
  more(part: 'field' | 'when'): void;
  /** Simple mode's note on an arrangement was asked to open Advanced. */
  openAdvanced?(): void;
  /** Something from the toolbox was let go over a section, at a place among its fields. */
  dropTool(spec: string, section: string, index: number): void;
  /** The app's own widgets, by `type` or `type.widget`. */
  widgets?: Record<string, WidgetFactory>;
}

export interface ScreenCanvas {
  element: HTMLElement;
  drag: CanvasDrag;
  /** Advanced's keys: moves said aloud, and the help that lists them. */
  keys: CanvasKeys;
  update(state: DesignerState): void;
  /** The sections on show, in order: at the top, and in the open tab of each set of tabs. */
  visibleSections(): string[];
  /** Put the cursor in a field's label or help on the canvas; every word of it selected, for a field just added. */
  focus(id: string, part: 'label' | 'help', selectAll?: boolean): void;
  /** Put the cursor in a section's title on the canvas, every word selected. */
  focusTitle(id: string): void;
  /** Put the cursor in the words of a part of a sheet's header, every word selected. */
  focusPart(id: string): void;
  /** Fill the page with the made-up record at `index`, or with nothing: drawn at the next update. */
  setSample(index: number | null): void;
  /** Simple or Advanced: drawn at the next update. */
  setMode(mode: DesignerMode): void;
  destroy(): void;
}

/** What sits at the top of a screen or a sheet: sections, a sheet's tabs, and fields and blocks of their own. */
const topOf = (page: Page) => (page.layout as { children: LayoutNode[] }).children;

export function screenCanvas(options: ScreenCanvasOptions): ScreenCanvas {
  const { designer, doc } = options;
  const el = elementFactory(doc);
  const words = designer.words;
  const w = words.canvas;
  /** The designer's own words on the screen drawn: its language and direction, when it has one. */
  const own = chromeLanguage(designer);
  const titleCard = el('button', { type: 'button', class: 'fd-canvas-title', hidden: '' });
  titleCard.addEventListener('click', () => designer.select(null));
  const body = el('div', { class: 'fd-canvas-body' });
  const header = canvasHeader({ el, doc, designer });
  // Several picked, in Advanced: a bar at the top of the canvas, taking no room.
  const multi = multiBar({ el, doc, designer });
  const keys = canvasKeys({ el, designer, rtl: () => doc.defaultView?.getComputedStyle(element).direction === 'rtl' });
  // A form of its own, in the form's skin: its look and its widths are the page's, not the designer's.
  // The size of screen shown, chosen on the stage at the canvas's top, with the keys' help; Advanced only.
  // Or a width of the canvas's own, dragged to on its end edge: the size is then the one the form takes it for.
  const win = doc.defaultView;
  let size: ScreenSize = readSize(win);
  let width: number | null = readWidth(win);
  /** A size picked, at its own width; or a width dragged or keyed to, at the size it falls in — kept in this browser when it is let go. */
  function showSize(next: ScreenSize, own: number | null, keep: boolean) {
    const redraw = next !== size;
    size = next;
    width = own;
    if (keep) {
      writeSize(win, next);
      writeWidth(win, own);
    }
    sizes.set(next, own);
    ownWidth();
    if (redraw) api.update(designer.getState());
  }
  const sizes = sizeSwitch(el, doc, size, (next) => showSize(next, null, true), width, words);
  const stage = el('div', { class: 'fd-canvas-stage', ...own }, sizes.element, sizes.note, keys.help);
  const element = el('div', { class: 'fd-canvas fd-form', 'data-fd-skin': options.skin ?? 'outlined' }, multi.element, stage, header.top, header.card, titleCard, body, keys.said);
  const resize = canvasResize({
    el,
    canvas: element,
    rtl: () => win?.getComputedStyle(element).direction === 'rtl',
    size: () => size,
    change: (next, at, done) => showSize(at, next, done),
    reset: () => showSize(size, null, true),
    say: keys.say,
    words,
  });
  /** The canvas as wide as its own width, in Advanced; else as wide as its size. */
  function ownWidth() {
    const own = mode === 'advanced' ? width : null;
    setData(element, 'width', own === null ? undefined : '');
    if (own === null) element.style.removeProperty('--fd-canvas-width');
    else element.style.setProperty('--fd-canvas-width', `${own}px`);
  }
  const blocks = blockViews({ el, doc, designer });
  const savedForms = formViews({ el, doc, designer, skin: options.skin, widgets: options.widgets });
  let page = designer.getPage();
  let selected: string | null = null;
  let picked: string[] = [];
  let mode: DesignerMode = 'simple';
  /** The size shown: the one chosen, in Advanced; a desktop, in Simple. */
  const shownSize = (): ScreenSize => (mode === 'advanced' ? size : 'desktop');
  let visible: string[] = [];
  /** The look the canvas wears now. */
  let worn: { look: Page['look'] } | null = null;
  /** Every part drawn this time round, by id. */
  let drawnIds = new Set<string>();
  /** The element each part of the page is drawn as, this time round: what a picked part's bar keeps clear of is found by it. */
  const shown = new Map<string, HTMLElement>();

  // The widgets are drawn from a form of the page, made again only when the page, or the record filling it, changes.
  let sample: number | null = null;
  let drawn: { page: Page; sample: number | null; form: Form } | null = null;
  const form = () => {
    if (drawn?.page === page && drawn.sample === sample) return drawn.form;
    const values = sample === null ? undefined : sampleRows(page.fields, Object.keys(page.fields), sample + 1)[sample];
    // The app's functions known, none called: nothing asks the app's server as a page is designed.
    drawn = { page, sample, form: createForm({ page, values, functions: uncalled(options.designer.functions()) }) };
    return drawn.form;
  };

  // ---- fields ------------------------------------------------------------------
  interface Card {
    element: HTMLElement;
    editing: boolean;
    widgetBox: HTMLElement;
    widget: Widget | null;
    painted: string;
    label: HTMLElement;
    help: HTMLElement;
    bar: FieldBar | null;
    options: OptionsEditor | null;
    settings: InlineSettings | null;
    /** What a card not being edited was drawn from: while all of it is the same, the card stays as it is. */
    drawn: { node: FieldNode; def: Field; labels: Place['labels']; picked: boolean; sample: number | null; language: string | undefined; quiet: boolean } | null;
  }
  const cards = new Map<string, Card>();
  /** Where a part sits in its grid: its place in it, and the grid's columns. */
  interface Where {
    index: number;
    columns: number;
  }

  /**
   * Counted as a part's view is made or taken away — a card, a group, tabs: the marks drawn on the parts are drawn
   * again only once this or what they say changed (`marked`), never for a key typed in a label that changes neither.
   */
  let built = 0;
  let marked: { built: number; page: Page } | null = null;

  function dropCard(card: Card) {
    built++;
    card.widget?.destroy?.();
    card.element.remove();
  }

  /** Six dots beside a field, shown when it is pointed at or picked: what it is carried by. For the pointer only — the keys move it too — so no control of its own. */
  const gripOf = () => el('span', { class: 'fd-card-grip', 'data-grip': '', 'aria-hidden': 'true', title: words.options.grip }, designerIcon(doc, 'grip'));

  function buildCard(id: string, editing: boolean, ownChoice: boolean): Card {
    built++;
    const widgetBox = el('div', { class: 'fd-canvas-widget', inert: '' });
    if (!editing) {
      const label = el('label', { class: 'fd-label' });
      const help = el('div', { class: 'fd-help' });
      // Reached by Tab and picked by Enter or Space, as a click picks it (WCAG 2.1.1).
      const element = el('div', { class: 'fd-field fd-canvas-field', 'data-node': id, role: 'button', tabindex: '0' }, gripOf(), label, widgetBox, help);
      return { element, editing, widgetBox, widget: null, painted: '', label, help, bar: null, options: null, settings: null, drawn: null };
    }
    const bar = fieldBar({ el, doc, designer, id, more: options.more });
    // As wide as its words, and wrapped where the field's width ends, never cut (growing-box.ts).
    const label = growingBox(el, { class: 'fd-canvas-label-input', 'data-inline': 'label', 'aria-label': w.label, autocomplete: 'off' });
    label.addEventListener('input', () => {
      sizeToWords(label);
      designer.updateQuestion(id, { label: label.value });
    });
    const help = el('input', { class: 'fd-canvas-help-input fd-help', 'data-inline': 'help', 'aria-label': w.help, placeholder: w.addHelp, autocomplete: 'off' });
    help.addEventListener('input', () => designer.updateQuestion(id, { help: (help as HTMLInputElement).value }));
    // Enter goes from the label to the help, and from the help out.
    label.addEventListener('keydown', (event) => {
      if ((event as KeyboardEvent).key === 'Enter') {
        event.preventDefault();
        (help as HTMLInputElement).focus();
      }
    });
    help.addEventListener('keydown', (event) => {
      if ((event as KeyboardEvent).key === 'Enter') {
        event.preventDefault();
        (help as HTMLInputElement).blur();
      }
    });
    const choices = ownChoice ? optionsEditor(el, designer, id) : null;
    const settings = inlineSettings(el, designer, id);
    const element = el(
      'div',
      { class: 'fd-field fd-canvas-field fd-editing', 'data-node': id },
      gripOf(),
      bar.element,
      el('div', { class: 'fd-label fd-canvas-label-row' }, label),
      ...(choices ? [choices.element] : [widgetBox]),
      settings.element,
      help
    );
    return { element, editing, widgetBox, widget: null, painted: '', label, help, bar, options: choices, settings, drawn: null };
  }

  /** As wide as its words, so the required mark stays beside them; wrapped where the field's width ends. */
  function sizeToWords(box: HTMLTextAreaElement) {
    box.cols = Math.max(4, box.value.length + 1);
  }

  function drawCard(node: FieldNode, labels: Place['labels'], place: Where, quietAround?: boolean): HTMLElement {
    const def = page.fields[node.field];
    const editing = selected === node.id;
    // Only the card being edited types its options in place: asked of it alone, as asking walks the page.
    const ownChoice = editing && def.type === 'selection' && !designer.isFromModel(node.id);
    let card = cards.get(node.id);
    if (card && (card.editing !== editing || (editing && !!card.options !== ownChoice))) {
      dropCard(card);
      card = undefined;
    }
    if (!card) cards.set(node.id, (card = buildCard(node.id, editing, ownChoice)));
    const element = card.element;
    // The quiet look as the form will wear it: the field's own word, else its group's, else the page's skin.
    const skin = page.look?.skin ?? (page.look?.theme ? THEME_SKINS[page.look.theme] : (options.skin ?? 'outlined'));
    const quietPlace = node.quiet ?? quietAround ?? skin === 'quiet';
    // A card not being edited, drawn from the same field, picked or not as before, for the same record, in the same
    // look: as it is — nothing of it read or written, so a key typed in another card costs it nothing.
    const isPicked = picked.includes(node.id) && picked.length > 1;
    const was = card.drawn;
    if (!editing && was?.node === node && was.def === def && was.labels === labels && was.picked === isPicked && was.sample === sample && was.language === page.language && was.quiet === quietPlace) return element;
    // Its sheet comes in a part of its own: the card is boxed until it has (look-parts.ts in @fieldia/widgets).
    if (quietPlace) wearQuiet(element.ownerDocument);
    element.toggleAttribute('data-in-quiet', quietPlace);
    element.toggleAttribute('data-quiet', !!def && wearsQuiet(def, node) && quietPlace);
    hint(card, node);
    card.drawn = editing ? null : { node, def, labels, picked: isPicked, sample, language: page.language, quiet: quietPlace };
    element.dataset['type'] = def.type;
    const labelsAt = labelPlace(node, def.type, labels);
    if (labelsAt) element.dataset['labels'] = labelsAt;
    else delete element.dataset['labels'];
    element.classList.toggle('fd-canvas-picked', isPicked);
    if (node.colspan) element.style.setProperty('--fd-span', String(node.colspan));
    else element.style.removeProperty('--fd-span');
    element.classList.toggle('fd-required', def.required === true || node.required === true);
    element.classList.toggle('fd-hidden-sometimes', node.invisible !== undefined);
    const labelText = node.label ?? def.label;
    const helpText = node.help ?? def.help ?? '';
    if (card.editing) {
      const label = card.label as HTMLTextAreaElement;
      const help = card.help as HTMLInputElement;
      if (doc.activeElement !== label) {
        label.value = labelText;
        sizeToWords(label);
      }
      if (doc.activeElement !== help) help.value = helpText;
      card.bar?.update(page);
      card.options?.update(def, node);
      // A table's columns, typed in, stand in for the table itself.
      card.widgetBox.hidden = card.settings?.update(page, node) === true;
    } else {
      card.label.textContent = labelText;
      card.help.textContent = helpText;
      card.help.hidden = !helpText;
    }
    if (!card.options) {
      paint(card, node);
      hint(card, node);
    }
    return element;
  }

  /** An empty quiet field says what to add, as the form will: its own words, else "Add story points". */
  function hint(card: Card, node: FieldNode) {
    const box = card.widgetBox.querySelector<HTMLInputElement | HTMLTextAreaElement>('input:not([type="checkbox"]):not([type="radio"]):not([type="file"]), textarea');
    const def = page.fields[node.field];
    if (!box || !def || node.placeholder || node.labels === 'hidden' || def.type === 'json' || def.type === 'properties') return;
    box.dataset['drawn'] ??= box.placeholder;
    // Written only when it changes: a card drawn again as it was is left alone.
    const words = card.element.hasAttribute('data-quiet') ? quietHint(node.label ?? def.label, widgetWords(page).locale) : box.dataset['drawn'];
    if (box.placeholder !== words) box.placeholder = words;
  }

  /** The field's real widget, inert: on the canvas it is looked at and moved, not typed in. */
  function paint(card: Card, node: FieldNode) {
    const def = page.fields[node.field];
    // Its own words in the page's language, as the form will have them.
    const said = widgetWords(designer.getPage());
    // Drawn again as what it draws changes: never for its label or help, which the card draws round it — a key typed in
    // a label makes no form of the whole page to draw the widget it leaves as it was.
    const { label: _label, help: _help, ...drawn } = node;
    const { label: _name, help: _words, ...kind } = def as Field & { help?: string };
    const key = JSON.stringify([drawn, kind, sample, said.locale]);
    if (card.painted === key && card.widget) return;
    card.painted = key;
    card.widget?.destroy?.();
    const f = form();
    const widget = createWidget({ form: f, name: node.field, field: def, node, id: `fd-canvas-${node.id}`, document: doc, ...said }, options.widgets);
    widget.update({ value: f.getState().values[node.field], values: f.getState().values, readonly: false, required: def.required === true, invalid: false });
    card.widget = widget;
    card.widgetBox.replaceChildren(widget.element);
  }

  // ---- sections ----------------------------------------------------------------
  interface SectionView {
    element: HTMLElement;
    legend: HTMLElement;
    title: HTMLButtonElement;
    titleInput: HTMLTextAreaElement;
    description: HTMLElement;
    grid: HTMLElement;
    empty: HTMLElement;
    lock: HTMLElement;
    fold: FoldMark;
  }
  const sections = new Map<string, SectionView>();

  /** A group is a fieldset, named by its title; an arrangement is no group to name, and a fieldset cannot lay parts on the columns round it. */
  function makeSection(id: string, tag: 'fieldset' | 'div'): SectionView {
    const title = el('button', { type: 'button', class: 'fd-canvas-section-title' });
    // A long title wraps where the group's width ends, never cut (growing-box.ts): on a phone its group is narrow.
    const titleInput = growingBox(el, { class: 'fd-canvas-section-title-input', 'aria-label': w.sectionTitle, placeholder: words.parts.untitledSection, autocomplete: 'off' });
    titleInput.addEventListener('input', () => designer.renameContainer(id, titleInput.value));
    titleInput.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') titleInput.blur();
    });
    title.addEventListener('click', () => {
      designer.select(id);
      titleInput.focus();
      titleInput.select();
    });
    const fold = foldMark(el, doc, words);
    const legend = el('legend', { class: 'fd-section-title' }, fold.element, title, titleInput);
    const description = el('p', { class: 'fd-section-description', hidden: '' });
    const grid = el('div', { class: 'fd-grid', 'data-drop-grid': '', 'data-container': id });
    const empty = el('p', { class: 'fd-canvas-empty', ...own }, w.dropHere);
    // Simple mode's word on an arrangement it keeps as Advanced laid it out.
    const advanced = el('button', { type: 'button', class: 'fd-button' }, w.openAdvanced);
    advanced.addEventListener('click', () => options.openAdvanced?.());
    const lock = el('div', { class: 'fd-simple-lock', hidden: '', ...own }, el('span', { class: 'fd-simple-lock-how' }), el('span', {}, w.simpleKeeps), advanced);
    const element = el(tag, { class: 'fd-section fd-canvas-section', 'data-node': id, 'data-drop-section': id }, legend, description, grid, empty, lock);
    return { element, legend, title, titleInput, description, grid, empty, lock, fold };
  }

  /** Where a part's colspan is, for the grid it sits in. */
  function span(element: HTMLElement, colspan: number | undefined) {
    if (colspan) element.style.setProperty('--fd-span', String(colspan));
    else element.style.removeProperty('--fd-span');
  }

  function drawSection(section: SectionNode, place: Place): HTMLElement {
    const plan = planSection(section, place);
    const tag = plan.arrangement ? 'div' : 'fieldset';
    let view = sections.get(section.id);
    if (view && view.element.tagName.toLowerCase() !== tag) {
      view.element.remove();
      view = undefined;
    }
    if (!view) {
      built++;
      sections.set(section.id, (view = makeSection(section.id, tag)));
    }
    visible.push(section.id);
    const element = view.element;
    setData(element, 'style', plan.style);
    setData(element, 'place', plan.at || undefined);
    setAttr(element, 'data-on-page', plan.style === 'card' && place.onPage ? '' : null);
    element.classList.toggle('fd-canvas-arrangement', plan.arrangement);
    span(element, section.colspan);
    if (section.labelWidth) element.style.setProperty('--fd-label-width', `${section.labelWidth}px`);
    else element.style.removeProperty('--fd-label-width');
    const isPicked = selected === section.id;
    element.classList.toggle('fd-canvas-selected', isPicked);
    element.classList.toggle('fd-canvas-picked', picked.includes(section.id) && picked.length > 1);
    // An arrangement has no title to show or type: it only holds parts.
    setHidden(view.legend, plan.arrangement);
    setText(view.title, section.title || words.parts.untitledSection);
    view.title.classList.toggle('fd-canvas-untitled', !section.title);
    setHidden(view.title, isPicked);
    setHidden(view.titleInput, !isPicked);
    if (doc.activeElement !== view.titleInput && view.titleInput.value !== (section.title ?? '')) view.titleInput.value = section.title ?? '';
    view.fold.update(section);
    setHidden(view.description, !section.description);
    setText(view.description, section.description ?? '');
    // On its grid's tracks it has no columns of its own; else its own, and those it keeps on smaller screens.
    const grid = view.grid;
    const columns = plan.at === 'tracks' ? null : section.columns;
    if (columns === null) grid.style.removeProperty('--fd-columns');
    else grid.style.setProperty('--fd-columns', String(typeof columns === 'object' ? columns.wide : (columns ?? 1)));
    // The columns of the size shown, whatever the canvas's own width: on a grid's tracks, or sharing a cell, as the form lays them.
    if (plan.at) grid.style.removeProperty('--fd-cols');
    else grid.style.setProperty('--fd-cols', String(columnsAt(section.columns, shownSize(), options.skin ?? 'outlined')));
    for (const width of ['medium', 'narrow'] as const) {
      const count = typeof columns === 'object' && columns !== null ? columns[width] : undefined;
      if (count === undefined) {
        grid.removeAttribute(`data-columns-${width}`);
        grid.style.removeProperty(`--fd-columns-${width}`);
      } else {
        grid.setAttribute(`data-columns-${width}`, String(count));
        grid.style.setProperty(`--fd-columns-${width}`, String(count));
      }
    }
    const inner = section.children.map((child, index) => drawItem(child, plan.inner, { index, columns: plan.inner.columns }));
    arrange(grid, inner);
    setHidden(view.empty, section.children.length > 0);
    const locked = mode === 'simple' && plan.arrangement && isPicked;
    setHidden(view.lock, !locked);
    if (locked) (view.lock.firstElementChild as HTMLElement).textContent = lockWords(page, section, words);
    return element;
  }

  /** Any part where it sits: a field, a group or an arrangement, tabs, or a block between fields. */
  function drawItem(node: LayoutNode, place: Place, at: Where): HTMLElement {
    drawnIds.add(node.id);
    const element = drawKind(node, place, at);
    shown.set(node.id, element);
    return element;
  }
  function drawKind(node: LayoutNode, place: Place, at: Where): HTMLElement {
    if (node.type === 'field') return drawCard(node, place.labels, at, place.quiet);
    if (node.type === 'section') return drawSection(node, place);
    if (node.type === 'tabs') return drawTabs(node, place);
    if (node.type === 'form') {
      const frame = savedForms.draw(node, selected === node.id);
      frame.classList.toggle('fd-canvas-picked', picked.includes(node.id) && picked.length > 1);
      return frame;
    }
    const element = blocks.draw(node, selected === node.id);
    element.classList.toggle('fd-canvas-selected', selected === node.id);
    element.classList.toggle('fd-canvas-picked', picked.includes(node.id) && picked.length > 1);
    return element;
  }

  /**
   * What a picked part's bar may reach, whose words it keeps clear of (bar-place.ts): the other parts of its row and
   * the rows above and below it in its group, read from the page's layout — and at its group's first or last row, the
   * group's name and what is past that edge, in what holds the group, up to the page's own head — each by the element
   * it is drawn as. A part of the record's head: the parts beside it, and what is above and below their row.
   */
  function nearOf(part: HTMLElement): readonly HTMLElement[] | undefined {
    const id = part.dataset['node'];
    if (!id || !shown.has(id)) {
      const row = part.parentElement;
      if (!row) return undefined;
      return [...row.children, row.previousElementSibling, row.nextElementSibling].filter((one): one is HTMLElement => one instanceof HTMLElement && one !== part);
    }
    const found: HTMLElement[] = [];
    const add = (node: { id: string }) => {
      const drawn = shown.get(node.id);
      if (drawn) found.push(drawn);
    };
    for (let at = id, depth = 0; depth < 12; depth++) {
      const spot = locate(page, at);
      if (!spot) break;
      const holder = spot.parent;
      const rows = rowsOf(page, holder);
      const row = rows.findIndex((one) => one.items.some((item) => item.id === at));
      for (const near of [rows[row - 1], rows[row], rows[row + 1]]) for (const item of near?.items ?? []) if (item.id !== at) add(item);
      if (row > 0 && row < rows.length - 1) return found;
      // At its group's edge: the group's name and description, a tab's tabs, and what is past the group, in what holds it.
      const group = sections.get(holder.id);
      if (group) found.push(group.legend, group.description);
      const tabs = tabsViews.get(holder.id);
      if (tabs) found.push(tabs.list, tabs.add);
      if (holder.id === page.layout.id) break;
      at = holder.id;
    }
    // At the page's own edge: what sits over its parts.
    found.push(header.top, header.card, titleCard, stage);
    return found;
  }

  // ---- tabs --------------------------------------------------------------------
  interface TabsView {
    element: HTMLElement;
    list: HTMLElement;
    panel: HTMLElement;
    grid: HTMLElement;
    add: HTMLButtonElement;
    active: string | null;
  }
  const tabsViews = new Map<string, TabsView>();

  function makeTabs(id: string): TabsView {
    const list = el('div', { class: 'fd-tablist', role: 'tablist' });
    const add = el('button', { type: 'button', class: 'fd-canvas-add-tab', 'aria-label': w.addTab, title: w.addTab }, '+');
    const grid = el('div', { class: 'fd-grid', 'data-drop-grid': '' });
    grid.style.setProperty('--fd-columns', '1');
    const panel = el('div', { class: 'fd-tabpanel', role: 'tabpanel' }, grid);
    const element = el('div', { class: 'fd-tabs fd-canvas-tabs', 'data-node': id }, el('div', { class: 'fd-canvas-tabs-head' }, list, add), panel);
    const view: TabsView = { element, list, panel, grid, add, active: null };
    add.addEventListener('click', () => {
      const tabs = locate(designer.getPage(), id)?.node as TabsNode | undefined;
      const created = tabs && designer.addTab(id, words.defaults.tab(tabs.children.length + 1));
      if (!created) return;
      view.active = created;
      designer.select(created);
    });
    return view;
  }

  function drawTabs(node: TabsNode, place: Place): HTMLElement {
    let view = tabsViews.get(node.id);
    if (!view) {
      built++;
      tabsViews.set(node.id, (view = makeTabs(node.id)));
    }
    const holding = node.children.find((tab) => tabHolds(tab, selected));
    if (holding) view.active = holding.id;
    if (!node.children.some((tab) => tab.id === view?.active)) view.active = node.children[0]?.id ?? null;
    const shown = view;
    shown.element.classList.toggle('fd-canvas-selected', selected === node.id);
    shown.element.classList.toggle('fd-canvas-picked', picked.includes(node.id) && picked.length > 1);
    span(shown.element, node.colspan);
    const buttons = node.children.map((tab) => {
      const button = el('button', { type: 'button', role: 'tab', class: 'fd-tab fd-canvas-tab', 'data-node': tab.id, 'aria-selected': String(tab.id === shown.active) }, tab.label || w.untitledTab);
      button.classList.toggle('fd-canvas-selected', selected === tab.id);
      button.addEventListener('click', () => {
        shown.active = tab.id;
        designer.select(tab.id);
      });
      return button;
    });
    shown.list.replaceChildren(...buttons);
    const open = node.children.find((tab) => tab.id === shown.active);
    // The open tab's parts sit where the tabs do: on the page, or in the box round them.
    if (open) {
      shown.panel.dataset['dropSection'] = open.id;
      shown.grid.dataset['container'] = open.id;
    }
    const inner: Place = { columns: 1, onPage: place.onPage, labels: place.labels, quiet: place.quiet };
    arrange(shown.grid, (open?.children ?? []).map((child, i) => drawItem(child, inner, { index: i, columns: 1 })));
    return shown.element;
  }

  /** Put these children in this order, moving only what is out of place. */
  /**
   * Put a container's parts in this order, moving only what is out of place: a part moved loses the focus,
   * so the one being typed in stays put. What the canvas draws over the parts (the guides) is left alone.
   */
  function arrange(parent: HTMLElement, children: HTMLElement[]) {
    const now = [...parent.children].filter((c) => !c.classList.contains('fd-guides')) as HTMLElement[];
    children.forEach((child, index) => {
      if (now[index] === child) return;
      parent.insertBefore(child, now[index] ?? null);
      const was = now.indexOf(child);
      if (was !== -1) now.splice(was, 1);
      now.splice(index, 0, child);
    });
    for (const extra of now.slice(children.length)) extra.remove();
  }

  // ---- picking -----------------------------------------------------------------
  element.addEventListener('click', (event) => {
    const target = event.target as Element;
    // The bar, the boxes and the lists of the field being edited handle themselves.
    if (target.closest('.fd-field-bar, input, textarea, select, button, .fd-q-option-box, .fd-multi')) return;
    // In Advanced, Shift, ⌘ or Ctrl adds a part to what is picked, or lets it go.
    const adding = mode === 'advanced' && (event.shiftKey || event.metaKey || event.ctrlKey);
    const any = target.closest<HTMLElement>('[data-node]');
    if (adding && any && body.contains(any) && any.getAttribute('role') !== 'tab') {
      designer.pick(any.dataset['node'] as string, { add: true });
      return;
    }
    const card = target.closest<HTMLElement>('.fd-canvas-field[data-node]');
    if (card) {
      const id = card.dataset['node'] as string;
      if (selected === id && picked.length <= 1) return;
      // Clicking a field's words opens it with the cursor in those words — in sight already, where it was clicked.
      const part = target.closest('.fd-label') ? 'label' : target.closest('.fd-help') ? 'help' : null;
      letGo(id);
      designer.select(id);
      if (part) focusSoon(id, part);
      return;
    }
    // A block, a group, an arrangement or tabs: the nearest part round what was clicked.
    const part = target.closest<HTMLElement>('[data-node]');
    if (part && body.contains(part) && part.getAttribute('role') !== 'tab') {
      const id = part.dataset['node'] as string;
      if (selected !== id || picked.length > 1) designer.select(id);
      return;
    }
    if (target === element || target === body) designer.select(null);
  });

  // A field not picked, focused: Enter or Space picks it, the cursor going to its label as a click on its words does.
  element.addEventListener('keydown', (event) => {
    const card = event.target as HTMLElement;
    if ((event.key !== 'Enter' && event.key !== ' ') || !card.matches('.fd-canvas-field[data-node]:not(.fd-editing)')) return;
    event.preventDefault();
    const id = card.dataset['node'] as string;
    letGo(id);
    designer.select(id);
    focusSoon(id, 'label');
  });

  /**
   * The focus let go from a card about to be drawn anew — the one picked, a card at rest until now, or the one picked
   * before, its words typed in — before anything is written: taken away with its card, the browser lays the whole
   * canvas out to move the focus (0.32: on each pick); let go once the page is written, it lays it out to blur (0.36).
   */
  function letGo(next: string | null) {
    const focused = doc.activeElement;
    const card = focused instanceof HTMLElement && body.contains(focused) ? focused.closest<HTMLElement>('.fd-canvas-field[data-node]') : null;
    if (card && card.classList.contains('fd-editing') !== (card.dataset['node'] === next)) (focused as HTMLElement).blur();
  }

  /**
   * The cursor put in a field's words as a press picks it (its words clicked, Enter or Space on its card): at the end
   * of the frame that draws it open, the page laid out — not at once, when the browser laid the whole canvas out a
   * second time to move the focus (0.36). A key pressed before then puts it there first, so what is typed lands in the
   * words; a press elsewhere first puts it where that press does.
   */
  let owed: { id: string; part: 'label' | 'help' } | null = null;
  function focusSoon(id: string, part: 'label' | 'help') {
    const view = doc.defaultView;
    owed = { id, part };
    if (!view?.requestAnimationFrame) return focusOwed();
    view.requestAnimationFrame(() => view.setTimeout(focusOwed, 0));
  }
  function focusOwed() {
    if (!owed || gone) return;
    const { id, part } = owed;
    owed = null;
    // Still the field picked, and open.
    if (selected === id) focusIn(id, part, false, true);
  }
  const keyFirst = () => owed && focusOwed();
  const pressFirst = () => (owed = null);
  doc.addEventListener('keydown', keyFirst, true);
  doc.addEventListener('pointerdown', pressFirst, true);

  /**
   * The cursor in a field's words. `seen`, the card was in sight as it was picked (pressed, or picked by the keys on
   * it): focused where it is, with no scroll to work out — else brought into sight as the browser's focus does.
   */
  function focusIn(id: string, part: 'label' | 'help', selectAll: boolean, seen = false) {
    const card = cards.get(id);
    if (!card?.editing) return;
    const input = (part === 'label' ? card.label : card.help) as HTMLInputElement;
    input.focus(seen ? { preventScroll: true } : undefined);
    if (selectAll) input.select();
    else input.setSelectionRange(input.value.length, input.value.length);
  }

  // Simple carries fields among fields, as before; Advanced drops any part beside, under, into or between others.
  const simpleDrag = canvasDrag({
    canvas: element,
    words,
    parts: '[data-node]:not([role="tab"])',
    enabled: () => mode === 'simple',
    drop: (source, section, index) => {
      if ('node' in source) {
        designer.placeNode(source.node, section, index, { pick: true });
      } else options.dropTool(source.tool, section, index);
    },
  });
  const advanced = advancedDrag({
    canvas: element,
    root: body,
    designer,
    rtl: () => doc.defaultView?.getComputedStyle(element).direction === 'rtl',
    // A new field comes with its name selected, to be typed over where it stands.
    placed: (id, source) => 'tool' in source && source.tool.startsWith('kind:') && focusIn(id, 'label', true),
  });
  advanced.setEnabled(false);
  // Advanced's width handle and gutter, on the part picked.
  // Advanced's guides: the columns of the grid the picked part sits on.
  const guides = canvasGuides({ root: body, designer });
  const widths = widthMarks({ canvas: element, root: body, designer, rtl: () => doc.defaultView?.getComputedStyle(element).direction === 'rtl' });
  /**
   * What is measured goes in the next frame, once the whole editor has written
   * this change, so the page is laid out once — measured now, it would be laid
   * out here and again after: the width handle goes where the part picked is,
   * and every floating bar on the canvas — the picked field's, a head part's —
   * goes where it belongs to its part (bar-place.ts), read before the handle is
   * written. The handle on the canvas's own edge says the canvas's width as it
   * now is, read with the rest and written last.
   */
  let widthsFor: DesignerState | null = null;
  let frameAsked = false;
  let gone = false;
  function nextFrame(state: DesignerState | null) {
    if (state) widthsFor = state;
    const view = win;
    if (!view?.requestAnimationFrame) {
      if (state) widths.update(state, mode === 'advanced');
      return;
    }
    if (frameAsked) return;
    frameAsked = true;
    view.requestAnimationFrame(() => {
      frameAsked = false;
      const latest = widthsFor;
      widthsFor = null;
      if (gone) return;
      // Read, all of it, before anything is written.
      const sized = mode === 'advanced' ? resize.read() : null;
      const bars = barsPlaced(element, nearOf);
      if (latest) widths.update(latest, mode === 'advanced');
      bars();
      sized?.();
    });
  }
  const resized = doc.defaultView?.ResizeObserver ? new doc.defaultView.ResizeObserver(() => nextFrame(null)) : null;
  resized?.observe(element);
  // The watch on the picked parts' sizes, ended with the canvas.
  const unwatch = () => stopWatchingBars(element);
  const drag: CanvasDrag = {
    press: (source, event, tile, lifted) => (mode === 'advanced' ? advanced : simpleDrag).press(source, event, tile, lifted),
    destroy() {
      simpleDrag.destroy();
      advanced.destroy();
    },
  };

  const api: ScreenCanvas = {
    element,
    drag,
    keys,
    setSample(index) {
      sample = index;
    },
    setMode(next) {
      mode = next;
      element.dataset['mode'] = next;
      advanced.setEnabled(next === 'advanced');
    },
    update(state) {
      // The focus in a card about to be drawn anew let go before the canvas is written (a pick on the canvas let it go
      // before anything was); the card picked takes it again once drawn.
      letGo(state.selected);
      setData(element, 'size', shownSize());
      ownWidth();
      page = state.page;
      selected = state.selected;
      picked = state.picked;
      visible = [];
      drawnIds = new Set();
      shown.clear();
      // The page's look, worn as the form wears it: an attribute or token for each setting, none left from before — when it changed.
      if (worn === null || worn.look !== page.look) {
        worn = { look: page.look };
        for (const name of ['font', 'density', 'corners', 'scheme', 'accent']) element.removeAttribute(`data-${name}`);
        for (const token of ['--fd-label-width', '--fd-look-accent', '--fd-look-accent-text', '--fd-look-accent-dark', '--fd-look-accent-dark-text']) element.style.removeProperty(token);
        applyLook(element, page.look);
      }
      // The page's own parts sit as the form puts them: one column, on the page — in a sheet, in its card.
      const root = page.layout;
      const sheet = root.type === 'sheet';
      setData(body, 'node', root.id);
      setData(body, 'container', root.id);
      body.classList.toggle('fd-sections', !sheet);
      const top: Place = { columns: 1, onPage: !sheet, labels: page.look?.labels };
      arrange(body, topOf(page).map((node, index) => drawItem(node, top, { index, columns: 1 })));
      // Gone from the page, or in a tab not on show: their views go.
      for (const [id, card] of cards) {
        if (drawnIds.has(id)) continue;
        dropCard(card);
        cards.delete(id);
      }
      for (const [id, view] of tabsViews) if (!drawnIds.has(id)) {
        built++;
        view.element.remove();
        tabsViews.delete(id);
      }
      const shownNow = new Set(visible);
      for (const [id, view] of sections) {
        if (shownNow.has(id)) continue;
        built++;
        view.element.remove();
        sections.delete(id);
      }
      blocks.keep(drawnIds);
      savedForms.keep(drawnIds);
      setHidden(titleCard, root.type !== 'sheet' || !root.title);
      if (root.type === 'sheet' && root.title) {
        // A made-up record names itself in the title; else the field's name stands in, as a placeholder does.
        const value = sample === null ? null : form().getState().values[root.title.field];
        titleCard.textContent = typeof value === 'string' && value ? value : (page.fields[root.title.field]?.label ?? root.title.field);
        titleCard.classList.toggle('fd-canvas-title-filled', typeof value === 'string' && !!value);
      }
      titleCard.classList.toggle('fd-canvas-selected', selected === null);
      header.update(page, selected, form);
      guides.update(state, mode === 'advanced');
      nextFrame(state);
      multi.update(state, mode === 'advanced');
      // The parts' marks: drawn again where a part's view was made or taken away, or what they say changed.
      if (marked?.built !== built || !sameMarks(marked.page, page, designer)) ruleMarks(element, page, designer);
      marked = { built, page };
      // steps lane: a button that does something wears a mark.
      pressMarks(element, page, designer);
    },
    visibleSections: () => [...visible],
    focus: (id, part, selectAll = false) => focusIn(id, part, selectAll),
    focusPart: (id) => header.focus(id),
    focusTitle(id) {
      const input = sections.get(id)?.titleInput;
      input?.focus();
      input?.select();
    },
    destroy() {
      gone = true;
      doc.removeEventListener('keydown', keyFirst, true);
      doc.removeEventListener('pointerdown', pressFirst, true);
      drag.destroy();
      widths.destroy();
      resize.destroy();
      header.destroy();
      resized?.disconnect();
      guides.destroy();
      unwatch();
      for (const card of cards.values()) dropCard(card);
      cards.clear();
      savedForms.destroy();
      sections.clear();
      tabsViews.clear();
      body.replaceChildren();
    },
  };
  return api;
}
